import { useCallback, useEffect, useRef, useState } from 'react';
import type { HostBridge } from '../../../host.js';
import {
  buildClientMetadata,
  buildEventMeta,
  submitSurveyResponse,
  type ShellHydration,
  type SubmitSurveyResponseResult,
} from '../../../api.js';
import type { PublicSurvey, SurveyAnswer } from '../types.js';
import { packAnswer } from '../types.js';

export type SubmissionStatus = 'idle' | 'submitting' | 'submitted';

interface SubmissionInput {
  survey: PublicSurvey;
  responses: Record<string, unknown>;
  email: string;
}

export interface UseSurveySubmissionResult {
  status: SubmissionStatus;
  error: string | null;
  setError: (message: string | null) => void;
  submit: (input: SubmissionInput) => Promise<boolean>;
  saveProgress: (input: SubmissionInput) => Promise<void>;
}

interface Params {
  bridge: HostBridge;
  hydration: ShellHydration;
  publicId: string;
}

const RESPONSE_ID_KEY_PREFIX = 'chirp:survey:responseId:';

export function useSurveySubmission({ bridge, hydration, publicId }: Params): UseSurveySubmissionResult {
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const responseIdRef = useRef<string | undefined>(readStoredResponseId(publicId));
  const inFlightRef = useRef<Promise<void> | null>(null);
  const startedAtRef = useRef<Date>(new Date());

  useEffect(() => {
    responseIdRef.current = readStoredResponseId(publicId);
    startedAtRef.current = new Date();
  }, [publicId]);

  const submit = useCallback(
    async (input: SubmissionInput): Promise<boolean> => {
      setStatus('submitting');
      setError(null);
      try {
        const result = await submitSurveyResponse(
          hydration,
          buildPayload(input, 'completed', responseIdRef.current, hydration, startedAtRef.current),
        );
        capturePersistedId(result, responseIdRef, publicId);
        clearStoredResponseId(publicId);
        bridge.send('chirp:survey:completed', { surveyId: publicId, response: result });
        setStatus('submitted');
        return true;
      } catch (submitError) {
        setStatus('idle');
        setError((submitError as Error).message);
        bridge.send('chirp:error', { code: 'survey_submit', message: (submitError as Error).message });
        return false;
      }
    },
    [bridge, hydration, publicId],
  );

  const saveProgress = useCallback(
    async (input: SubmissionInput): Promise<void> => {
      if (input.survey.partialSaveEnabled === false) return;
      if (inFlightRef.current) await inFlightRef.current;
      const task = submitSurveyResponse(
        hydration,
        buildPayload(input, 'partial', responseIdRef.current, hydration, startedAtRef.current),
      )
        .then((result) => {
          capturePersistedId(result, responseIdRef, publicId);
        })
        .catch(() => {
          // Partial saves are best-effort; drop errors silently.
        })
        .finally(() => {
          inFlightRef.current = null;
        });
      inFlightRef.current = task;
      await task;
    },
    [hydration, publicId],
  );

  return { status, error, setError, submit, saveProgress };
}

function buildPayload(
  input: SubmissionInput,
  status: 'partial' | 'completed',
  responseId: string | undefined,
  hydration: ShellHydration,
  startedAt: Date,
) {
  const answers: SurveyAnswer[] = Object.entries(input.responses).map(([key, value]) =>
    packAnswer(key, value, input.survey.questions ?? []),
  );
  const resolvedEmail = input.email?.trim() || hydration.user?.email || undefined;
  const uniqueId = hydration.user?.uniqueId || undefined;
  const client = buildClientMetadata(hydration.host?.url);
  const completedAt = new Date();
  const timeSpentSeconds = Math.max(
    0,
    Math.round((completedAt.getTime() - startedAt.getTime()) / 1000),
  );
  const meta: Record<string, unknown> = {
    ...buildEventMeta(hydration),
    sessionId: hydration.sessionId,
    screenWidth: client.screenWidth,
    screenHeight: client.screenHeight,
    language: client.language,
    timezone: client.timezone,
    startedAt: startedAt.toISOString(),
    completedAt: completedAt.toISOString(),
    timeSpentSeconds,
  };
  return {
    publicId: input.survey._id,
    answers,
    email: resolvedEmail,
    uniqueId,
    responseId,
    status,
    meta,
  };
}

function capturePersistedId(
  result: SubmitSurveyResponseResult | null,
  ref: { current: string | undefined },
  publicId: string,
): void {
  if (!result?._id) return;
  if (!ref.current) ref.current = result._id;
  writeStoredResponseId(publicId, result._id);
}

function readStoredResponseId(publicId: string): string | undefined {
  if (typeof window === 'undefined' || !window.sessionStorage) return undefined;
  try {
    return window.sessionStorage.getItem(RESPONSE_ID_KEY_PREFIX + publicId) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStoredResponseId(publicId: string, responseId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(RESPONSE_ID_KEY_PREFIX + publicId, responseId);
  } catch {
    // quota exceeded — ignore
  }
}

function clearStoredResponseId(publicId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(RESPONSE_ID_KEY_PREFIX + publicId);
  } catch {
    // ignore
  }
}
